/** @jsx jsx */
import React, { memo } from 'react';
import { jsx } from '@emotion/core';
import styled from '@emotion/styled';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { useLeaflet } from 'react-leaflet';

import { isMediaQuery } from '~/core/scripts/mediaquery-helper';
import Icon from '~/core/scripts/components/Icon';
import Utils from '~/core/scripts/utils';
import { clamp } from '~/core/scripts/chart-components/utils';
import Histogram from '~/components/Histogram';

import { Button } from '~/styles/index.styles';
import * as S from './Tooltip.styles';

const TooltipHistogram = styled.div`
  flex-basis: 100%;
  margin-top: 16px;
`;

const TOOLTIP_WIDTH = 320;
const TOOLTIP_HEIGHT = 230;

const Tooltip = () => {
  const { map } = useLeaflet();
  const values = useStoreState((state) => state.app.currentValues);
  const position = useStoreState((state) => state.tooltip.position);
  const data = useStoreState((state) => state.tooltip.data);
  const isVisible = useStoreState((state) => state.tooltip.isVisible);
  const isStatic = useStoreState((state) => state.tooltip.isStatic);
  const setVisible = useStoreActions((actions) => actions.tooltip.setVisible);
  const setStatic = useStoreActions((actions) => actions.tooltip.setStatic);
  const setKreisSearched = useStoreActions(
    (actions) => actions.app.setKreisSearched
  );
  const setKreisSearchValue = useStoreActions(
    (state) => state.app.setKreisSearchValue
  );
  const colorScale = useStoreState((state) => state.app.colorScale);
  const selectedIndicator = useStoreState(
    (state) => state.app.selectedIndicator
  );

  if (!data) {
    return null;
  }

  const style = isMediaQuery(769)
    ? {
        left: `${position.x + 15}px`,
        top: `${clamp(position.y - 75, 0, map.getSize().y - TOOLTIP_HEIGHT)}px`,
      }
    : {};

  const isInverted = position.x + 35 + TOOLTIP_WIDTH >= map.getSize().x;

  const isClosable = Utils.isTouch || !isMediaQuery(769) || isStatic;

  const { calculateValue, unit, decimals, shortLabel } = selectedIndicator;
  const currValue = calculateValue([data]);
  const title = data.Name;
  const subtitle = data.Bundesland;

  const handleClose = (evt) => {
    evt.preventDefault();
    evt.stopPropagation();
    setKreisSearchValue(null);
    setStatic(false);
    setVisible(false);
    setKreisSearched(null);
  };

  return (
    <S.TooltipWrapper
      isVisible={isVisible}
      isInverted={isInverted}
      isFixedOnMobile
      style={{ ...style, borderLeftColor: colorScale(currValue) }}
      isClosable={isClosable}
    >
      <div style={{ position: 'relative' }}>
        {isClosable && (
          <Button css={S.tooltipClose} close onClick={handleClose}>
            <Icon type="close" />
          </Button>
        )}
        <S.TooltipHeader>
          <S.TooltipTitle
            dangerouslySetInnerHTML={{ __html: `${shortLabel} in ${title}` }}
          />
          <S.TooltipSubtitle>{subtitle}</S.TooltipSubtitle>
        </S.TooltipHeader>
        {Number.isNaN(currValue) ? (
          <S.TooltipBody>Keine Daten vorhanden</S.TooltipBody>
        ) : (
          <S.TooltipBody>
            {/* <S.TooltipLabel>{label}</S.TooltipLabel> */}
            <S.TooltipValue>
              {Utils.numberFormat(currValue, decimals)}
            </S.TooltipValue>
            <S.TooltipUnit>{unit}</S.TooltipUnit>
            <TooltipHistogram>
              <Histogram
                values={values}
                highlightValue={currValue}
                highlightLabel={title}
                unit={unit}
                decimals={decimals}
              />
            </TooltipHistogram>
          </S.TooltipBody>
        )}
      </div>
    </S.TooltipWrapper>
  );
};

Tooltip.displayName = 'Tooltip';

export default Tooltip;
